/**
 * Phase 7 — Gamification panel embedded in the Winhouse Dashboard.
 *  - Level progress ring
 *  - Recent achievements
 *  - Leaderboard top-5
 *  - Username setup modal (first-time)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Flame, Sparkles, Award, ShieldCheck, Crown, Star,
  BookOpen, Calculator, CheckCircle2, BookOpenText, Loader2, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ICON_MAP = {
  Trophy, Flame, Sparkles, Award, ShieldCheck, Crown, Star,
  BookOpen, Calculator, CheckCircle2, BookOpenText,
};

const LevelRing = ({ level, pct }) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(2, Math.min(100, pct)) / 100);
  return (
    <div className="relative w-16 h-16 shrink-0" data-testid="lvl-ring">
      <svg className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="5" fill="none" />
        <circle cx="32" cy="32" r={r} stroke="#D3D86F" strokeWidth="5" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">{level}</div>
    </div>
  );
};

const UsernameModal = ({ currentEmail, onClose, onSet }) => {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    if (!val) { setAvailable(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await ax.get(`/api/public/username-available?username=${encodeURIComponent(val)}`);
        setAvailable(r.data?.available);
        setHint(r.data?.reason || null);
      } catch (_e) { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
  }, [val]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/me/student/username', { username: val });
      toast.success(`Username set to ${r.data.username} 🎉`);
      onSet(r.data.username);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not set username'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="username-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold flex items-center gap-2"><Crown className="h-5 w-5 text-amber-600" /> Pick a public handle</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="username-close"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">This is what the leaderboard will show. Your real name stays private.</p>
        <input value={val} onChange={e => setVal(e.target.value)} placeholder="e.g. SelectiveLegend"
          className="w-full border-2 border-gray-200 focus:border-black outline-none rounded-xl px-3 py-2 text-sm font-mono"
          data-testid="username-input" maxLength={19} />
        <div className="mt-1.5 text-[11px] h-4">
          {val && available === true && <span className="text-emerald-700 font-bold">✓ Available</span>}
          {val && available === false && <span className="text-rose-700 font-bold">✗ {hint || 'Taken'}</span>}
        </div>
        <ul className="text-[10px] text-gray-500 list-disc pl-4 space-y-0.5 mb-3">
          <li>3–19 characters · letters / numbers / underscore</li>
          <li>Start with a letter · no real names required</li>
          <li>No profanity (auto-checked)</li>
        </ul>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Maybe later</button>
          <button onClick={submit} disabled={busy || available !== true}
            className="text-xs bg-black text-[#D3D86F] hover:bg-gray-900 disabled:opacity-40 px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1"
            data-testid="username-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Set handle
          </button>
        </div>
      </div>
    </div>
  );
};

const GamificationPanel = () => {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [askUsername, setAskUsername] = useState(false);

  const refetch = async () => {
    try {
      const [g, b] = await Promise.all([
        ax.get('/api/me/student/gamification'),
        ax.get('/api/leaderboard?limit=5'),
      ]);
      setData(g.data);
      setBoard(b.data?.items || []);
      if (!g.data?.username) {
        // Politely nudge once per session
        const k = 'wh_username_prompt_v1';
        if (!sessionStorage.getItem(k)) {
          setAskUsername(true);
          sessionStorage.setItem(k, '1');
        }
      }
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { refetch(); }, []);

  if (!data) return null;
  const L = data.level || {};

  return (
    <section className="bg-gradient-to-br from-black via-[#0a0a0a] to-black text-white rounded-2xl border border-[#D3D86F]/40 p-5 relative overflow-hidden" data-testid="gam-panel">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
        background: 'radial-gradient(60% 50% at 100% 0%, rgba(220,38,38,0.25) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 0% 100%, rgba(211,216,111,0.20) 0%, rgba(211,216,111,0) 70%)',
      }} />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">Your progress</div>
            <h3 className="text-lg font-bold mt-0.5">
              {L.name} {data.username && <span className="text-white/60 text-[11px] font-normal ml-1">· @{data.username}</span>}
            </h3>
          </div>
          {!data.username && (
            <button onClick={() => setAskUsername(true)} data-testid="gam-set-username"
              className="text-[10px] bg-[#D3D86F] hover:bg-[#c4ca5e] text-black px-3 py-1.5 rounded-full font-bold uppercase tracking-wider">
              Set leaderboard name
            </button>
          )}
        </div>

        <div className="flex items-center gap-4 mb-5">
          <LevelRing level={L.level || 1} pct={L.progress_pct || 0} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[2px] text-white/65 font-bold">XP</div>
            <div className="text-2xl font-bold">{data.total_xp}</div>
            {L.next_min_xp != null && (
              <div className="text-[11px] text-white/60 mt-0.5">{L.next_min_xp - data.total_xp} XP to {L.next_level && LEVEL_NAMES[L.next_level - 1] ? LEVEL_NAMES[L.next_level - 1] : 'next level'}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[2px] text-white/65 font-bold flex items-center gap-1 justify-end"><Flame className="h-3 w-3 text-amber-400" /> Streak</div>
            <div className="text-2xl font-bold">{data.streak_days}d</div>
            <div className="text-[10px] text-white/55">Longest {data.longest_streak}d</div>
          </div>
        </div>

        {/* Badges */}
        <div className="mb-5" data-testid="gam-badges">
          <div className="text-[10px] uppercase tracking-[2px] text-[#D3D86F] font-bold mb-2 flex items-center gap-1">
            <Award className="h-3 w-3" /> Achievements ({data.achievements?.length || 0}/{data.achievement_catalog?.length || 8})
          </div>
          {data.achievements?.length === 0 ? (
            <p className="text-[11px] text-white/55 italic">Complete a topic, ace a quiz or maintain a 7-day streak to unlock badges.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {data.achievements.slice(0, 8).map(a => {
                const Icon = ICON_MAP[a.icon] || Award;
                return (
                  <span key={a.id} className="bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-[10px] inline-flex items-center gap-1.5" data-testid={`gam-badge-${a.achievement_id}`}>
                    <Icon className="h-3 w-3 text-[#D3D86F]" />
                    <span className="text-white/90">{a.name}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Leaderboard preview */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid="gam-leaderboard">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[2px] text-[#D3D86F] font-bold flex items-center gap-1"><Trophy className="h-3 w-3" /> Leaderboard</div>
            <Link to="/student/exams?tab=leaderboard" className="text-[10px] text-[#D3D86F] hover:underline">View all →</Link>
          </div>
          {board.length === 0 ? (
            <p className="text-[11px] text-white/55 italic">Be the first on the leaderboard — set a public handle to opt in.</p>
          ) : (
            <ol className="space-y-1.5">
              {board.slice(0, 5).map(r => {
                const isMe = data.username && r.username === data.username;
                return (
                  <li key={r.rank} className={`flex items-center justify-between text-sm ${isMe ? 'text-[#D3D86F]' : 'text-white/85'}`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${r.rank === 1 ? 'bg-[#D3D86F] text-black' : 'bg-white/10'}`}>{r.rank}</span>
                      <span className="font-bold">{r.username}</span>
                      {isMe && <span className="text-[9px] uppercase tracking-wider bg-[#D3D86F] text-black px-1.5 py-0.5 rounded">You</span>}
                    </span>
                    <span className="text-[11px]"><b>{r.xp}</b> XP · L{r.level}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
      {askUsername && <UsernameModal onClose={() => setAskUsername(false)} onSet={() => { setAskUsername(false); refetch(); }} />}
    </section>
  );
};

const LEVEL_NAMES = ['Explorer', 'Learner', 'Achiever', 'Scholar', 'Master', 'Champion', 'Legend'];

export default GamificationPanel;
